import { localStore } from 'utils/storage'
import { REFERRAL_ATTRIBUTION_WINDOW_DAYS } from './constants'

// AffiliateWP ref id. On `.creativefabrica.com` it lives in the `affwp_ref`
// cookie, which the GraphQL gateway reads server-side. Apps that can't share
// that cookie cross-domain capture the ref from the landing URL into
// localStorage and forward it explicitly on the payments mutations — the BE
// still reads the cookie when present, so we deliberately don't read the
// cookie here to avoid sending it back over the wire.

// Versioned per the storage conventions so the schema can evolve without clashes.
const AFFILIATE_ID_STORAGE_KEY = 'cf:affiliate-id:v1'

// Mirror the AffiliateWP cookie's attribution window so a stored ref can't
// outlive the commission window and leak into payments long after it expired.
const AFFILIATE_ID_TTL_MS = REFERRAL_ATTRIBUTION_WINDOW_DAYS * 24 * 60 * 60 * 1000

// Non-pretty AffiliateWP link: ?affwp_ref=<id>
const AFFILIATE_ID_QUERY_PARAM = 'affwp_ref'
// Pretty affiliate link path segment: /ref/<id>
const AFFILIATE_REF_PATH_PATTERN = /(?:^|\/)ref\/([^/?#]+)/

interface StoredAffiliateId {
  id: string
  expiresAt: number
}

/**
 * Returns the affiliate id captured from a landing URL into localStorage,
 * ignoring entries past the attribution window. Returns null when nothing is
 * stored — callers must not fall back to the `affwp_ref` cookie, because the
 * BE reads that cookie itself and forwarding it would be redundant.
 */
export function getAffiliateIdFromStorage(): string | null {
  const stored = localStore.getJSON<StoredAffiliateId>(AFFILIATE_ID_STORAGE_KEY)
  if (!stored || typeof stored.id !== 'string' || typeof stored.expiresAt !== 'number') {
    return null
  }

  if (Date.now() > stored.expiresAt) {
    localStore.remove(AFFILIATE_ID_STORAGE_KEY)
    return null
  }

  return stored.id
}

/**
 * Persists the affiliate id to localStorage with a TTL matching the AffiliateWP
 * attribution window. No-ops on empty input.
 */
export function setAffiliateId(affiliateId: string): void {
  if (!affiliateId) {
    return
  }
  localStore.setJSON<StoredAffiliateId>(AFFILIATE_ID_STORAGE_KEY, {
    id: affiliateId,
    expiresAt: Date.now() + AFFILIATE_ID_TTL_MS,
  })
}

/**
 * Extracts an affiliate id from a URL, supporting both the `?affwp_ref=<id>`
 * query param and the pretty `/ref/<id>` path segment. Returns null when neither
 * is present or the URL can't be parsed.
 */
export function readAffiliateIdFromUrl(url: string): string | null {
  let parsed: URL
  try {
    parsed = new URL(url)
  } catch {
    return null
  }

  const fromQuery = parsed.searchParams.get(AFFILIATE_ID_QUERY_PARAM)
  if (fromQuery) {
    return fromQuery
  }

  const match = AFFILIATE_REF_PATH_PATTERN.exec(parsed.pathname)
  if (!match) {
    return null
  }
  try {
    return decodeURIComponent(match[1])
  } catch {
    return match[1]
  }
}

/**
 * Reads the affiliate id from the given URL (defaults to the current location)
 * and, when found, persists it to localStorage. Returns the captured id, if any.
 */
export function captureAffiliateIdFromUrl(url: string = window.location.href): string | null {
  const affiliateId = readAffiliateIdFromUrl(url)
  if (affiliateId) {
    setAffiliateId(affiliateId)
  }
  return affiliateId
}

/**
 * Detects the pretty `/ref/<id>` affiliate-link shape at the end of a pathname.
 * Matches both the bare `/ref/<id>` form and deep links like
 * `/<some>/<deep>/ref/<id>`. Returns `null` when the pathname doesn't end in
 * the `/ref/<id>` shape — callers use this as the gate for whether to handle a
 * request as an affiliate landing.
 *
 * Pathname-only by design: this is the routing-layer companion to
 * `readAffiliateIdFromUrl`, which also covers the `?affwp_ref=<id>` query
 * form. Query landings don't need URL rewriting, so they're handled at app
 * init via `captureAffiliateIdFromUrl()` rather than here.
 */
export function parseAffiliateRefFromPathname(
  pathname: string,
): { id: string; prefix: string } | null {
  const trimmed = pathname.replace(/\/$/, '')
  const lastSlash = trimmed.lastIndexOf('/')
  if (lastSlash < 0) {
    return null
  }
  const id = trimmed.slice(lastSlash + 1)
  const prefix = trimmed.slice(0, lastSlash)
  if (!id || !prefix.endsWith('/ref')) {
    return null
  }
  return { id, prefix }
}

/**
 * Removes the `/ref/<id>` segment from a URL's pathname and the AffiliateWP
 * `campaign` param from its query string. Returns the URL the user should land
 * on after the affiliate ref has been captured.
 *
 * Kept as a pure string operation (no `URL` parsing) so the shared
 * `@repo/affiliate-id-vite/capture-ref` route helper can preserve Studio's
 * existing URL-cleaning behavior.
 */
export function stripAffiliateRefFromUrl(url: string, id: string): string {
  // remove the campaign query param from the URL if present
  const urlSegments = url.split('?')
  let urlSearchParamsString = ''
  if (urlSegments.length > 1) {
    const urlSearchParams = new URLSearchParams(`?${urlSegments[1]}`)
    const campaign = urlSearchParams.get('campaign')
    if (campaign) {
      urlSearchParams.delete('campaign')
    }
    urlSearchParamsString = urlSearchParams.toString()
  }
  return `${urlSegments[0].replace(`/ref/${id}`, '')}${
    urlSearchParamsString.length > 0 ? `?${urlSearchParamsString}` : ''
  }`
}
